import { ICustomFieldSchema } from '@/api/customField';
import { CheckBoxField } from '@/components/form/fields/CheckBoxField';
import { InputField } from '@/components/form/fields/InputField';
import { RadioField } from '@/components/form/fields/RadioField';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import {
  customFieldTypeOptions,
  ModuleType,
} from '@/utils/constants/customField';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { CustomOptionSchema } from './FormCustomField';
import { useEffect } from 'react';
import { LinkButton } from '@/components/shared/button/LinkButton';

type ICustomFieldOptionProps = {
  customFieldTypes: string;
  moduleTypeValue: string;
  isMandatoryData?: boolean;
  isSearchableData?: boolean;
  customOptionsData?: CustomOptionSchema[];
};

export const CustomFieldOption: React.FC<ICustomFieldOptionProps> = ({
  customFieldTypes,
  moduleTypeValue,
  isMandatoryData,
  isSearchableData,
  customOptionsData,
}) => {
  const form = useFormContext<ICustomFieldSchema>();
  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: 'options',
  });

  useEffect(() => {
    const updateOptions = () => {
      if (customOptionsData && customOptionsData.length > 0) {
        replace(
          customOptionsData.map(option => ({
            option: option.option, // asumsikan customOptionsData memiliki struktur { option: string }
          })),
        );
      } else if (fields.length === 0) {
        append({
          option: null,
        });
      }
    };

    updateOptions();
    form.trigger('options');
  }, [customOptionsData]);

  const fieldOptions = customFieldTypeOptions
    .filter(field => field.value === customFieldTypes)
    .map(field => field.isHaveOption)
    .join('');

  return customFieldTypes === '' && moduleTypeValue !== ModuleType.TEST_RUN ? (
    <div className="flex justify-center p-5 text-lg">
      Select a custom field first
    </div>
  ) : (
    <div className="flex flex-col gap-4 mt-2">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <InputField
            className="w-3/4"
            name="label_field"
            placeholder="Enter Label Name"
            key="label_field"
            label="Label Fields"
            hasRequiredAsterisk
          />
        </div>
        {moduleTypeValue !== ModuleType.TEST_RUN && (
          <>
            <div>
              <CheckBoxField
                name="is_mandatory"
                label="Mandatory Fields"
                defaultValue={isMandatoryData === false ? [] : ['true']}
                key="is_mandatory"
                options={[{ label: 'Is mandatory', value: 'true' }]}
              />
            </div>
            <div>
              <RadioField
                name="is_searchable"
                label="Searchable Field"
                defaultValue={isSearchableData === false ? 'false' : 'true'}
                hasRequiredAsterisk
                options={[
                  { key: 'true', label: 'Yes' },
                  { key: 'false', label: 'No' },
                ]}
              />
            </div>
          </>
        )}
      </div>
      {(fieldOptions === 'true' || moduleTypeValue === ModuleType.TEST_RUN) && (
        <div className="p-5 border-2">
          <div className="flex justify-end">
            <OutlineButton
              icon={<FaPlus />}
              onClick={() => {
                append({
                  option: null,
                });

                form.trigger('options');
              }}
            >
              Add More
            </OutlineButton>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {fields.map((field, index) => (
              <div key={field.id} className="flex items-end gap-1">
                <div className="w-5/6">
                  <InputField
                    name={`options.${index}.option`}
                    placeholder="Enter Options"
                    label={index == 0 ? 'Options' : ''}
                    hasRequiredAsterisk={index == 0}
                  />
                </div>
                <div className="mb-2 ml-2">
                  {index >= 1 && (
                    <LinkButton
                      onClick={() => {
                        if (fields.length > 1) remove(index);
                      }}
                      className="text-red-500"
                      icon={<FaTrash size={15} />}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
